/**
 * @file Internal singleton for managing the RxDB database instance.
 * This composable handles the entire lifecycle of the database, ensuring it is
 * initialized only once and providing asynchronous access to collections and utilities.
 * @since 2025-07-03
 */

const { ref, shallowRef, computed } = Vue;
const {
  createRxDatabase,
  addRxPlugin,
  RxDBDevModePlugin,
  RxDBQueryBuilderPlugin,
  RxDBLeaderElectionPlugin,
  RxDBMigrationSchemaPlugin,
  getRxStorageDexie,
  wrappedValidateAjvStorage,
  RxDBStatePlugin,
  getAjv,
} = RxDB;
import { DatabaseError } from '../utils/errors.js';
import { isLocalhost } from '../sharepoint/utils.js';

// Add RxDB plugins
if (isLocalhost()) {
  addRxPlugin(RxDBDevModePlugin);
}
addRxPlugin(RxDBQueryBuilderPlugin);
addRxPlugin(RxDBLeaderElectionPlugin);
addRxPlugin(RxDBMigrationSchemaPlugin);
addRxPlugin(RxDBStatePlugin);

const ajv = getAjv();
ajv.addFormat('color', /^#[0-9a-fA-F]{6}$/);

// Simple state management
let _initializeResolve = null;
let _initializeReject = null;
const _initializePromise = new Promise((resolve, reject) => {
  _initializeResolve = resolve;
  _initializeReject = reject;
});

let _rxDatabase = null;

// Global event emitter for all collections
const dbEvents = new EventTarget();

// Reactive arrays for collections and configs
const _collections = ref([]);
const _configs = ref([]);

// Database instance object - always available as a reactive object
const dbInstance = Vue.reactive({
  // Reactive state
  isLoading: true,
  isInitialized: false,
  error: null,
  collections: _collections,
  configs: _configs,
  rawDb: null,
  config: {},
  events: dbEvents,

  // Aggregated sync status
  isSyncing: computed(() => {
    return _collections.value.some(col => col.isSyncing);
  }),

  isMigrating: computed(() => {
    return _collections.value.some(col => col.isMigrating);
  }),

  isReady: computed(() => {
    return _collections.value.every(col => col.isReady);
  }),

  isInSync: computed(() => {
    return _collections.value.every(col => col.isInSync || col.isLocalOnly);
  }),

  hasErrors: computed(() => {
    return _collections.value.some(col => col.syncErrors.length > 0);
  }),

  // Core functions
  async ensureInitialized() {
    return _initializePromise;
  },

  // Advanced access
  async getRawDbInstance() {
    await _initializePromise;
    return _rxDatabase;
  },

  // Sync methods
  async resyncAll() {
    const promises = _collections.value.map(col => {
      return col.resync().catch(err => {
        console.warn(`Failed to resync collection '${col.name}':`, err);
      });
    });
    return Promise.all(promises);
  },

  async hardReset() {
    if (
      !confirm(
        'Are you sure you want to hard reset the database? This will delete all local data and reload the page.'
      )
    ) {
      return;
    }
    await _initializePromise;
    await _rxDatabase.remove();
    location.reload();
  },
});

/**
 * Initializes the database with the provided configuration.
 * Can only be called once per application.
 * @param {object} options - Configuration options for database initialization
 * @returns The database instance.
 */
export function initDb(options = {}) {
  if (dbInstance.isInitialized) {
    throw new DatabaseError(
      'Database already initialized. Use useDb() to access the existing instance.',
      'initialization'
    );
  }

  const {
    name = 'sharepoint-sync-v9',
    multiInstance = true,
    eventReduce = true,
    storage = wrappedValidateAjvStorage({ storage: getRxStorageDexie() }),
    ...rxdbOptions
  } = options;

  dbInstance.config = options;

  // Start initialization process
  console.log('Starting database initialization...');
  dbInstance.isLoading = true;
  dbInstance.error = null;

  const startTime = performance.now();

  // Initialize asynchronously
  (async () => {
    try {
      // Create RxDB database
      console.log('Creating RxDB database instance...');
      _rxDatabase = await createRxDatabase({
        name,
        storage,
        multiInstance,
        eventReduce,
        ...rxdbOptions,
      });

      dbInstance.rawDb = Vue.markRaw(_rxDatabase);

      const endTime = performance.now();
      console.log(
        `Database initialization complete in ${Math.round(endTime - startTime)}ms`
      );
      dbInstance.isInitialized = true;
      _initializeResolve();
    } catch (err) {
      const endTime = performance.now();
      console.error(
        `Database initialization failed after ${Math.round(endTime - startTime)}ms:`,
        err
      );
      dbInstance.error = err;
      _initializeReject(err);
    } finally {
      dbInstance.isLoading = false;
    }
  })();

  return dbInstance;
}

let monitorInit = null;

/**
 * Returns the database instance.
 * Always returns a consistent object reference, even before initDb() is called.
 * The instance will be in an uninitialized state until initDb() is called.
 * @returns The database instance.
 */
export function useDb() {
  if (monitorInit === null) {
    monitorInit = setTimeout(() => {
      if (!dbInstance.isInitialized) {
        console.error(
          `[Airlift] Database not initialized within 5 seconds.\n` +
            `Make sure to call initDb() in your application initialization code.`
        );
      }
    }, 5000);
  }
  return dbInstance;
}

// Backward compatibility
export const createDb = initDb;
